import { useTranslation } from '@rhino-project/core/i18n';
import { ComponentPropsWithoutRef, useEffect, useState } from 'react';

import { useRhinoAsset, useRhinoConfig } from '@rhino-project/core/config';

interface ThemedLogoProps {
  className?: string;
}

const getTheme = () =>
  typeof document !== 'undefined' &&
  (document.documentElement.classList.contains('dark') ||
    document.documentElement.getAttribute('data-theme') === 'dark')
    ? 'dark'
    : 'light';

export const ThemedLogo = ({ className }: ThemedLogoProps) => {
  // The theme guard has set the class before first paint, so read it now
  // rather than after mount, which would flash the dark logo on a dark page.
  const [theme, setTheme] = useState(getTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(getTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Show the dark logo on a light theme and the light logo on a dark theme.
  // className is forwarded straight to the chosen logo (the underlying <img>),
  // so callers control sizing without this component imposing any.
  return theme === 'light' ? (
    <DarkLogo className={className} />
  ) : (
    <LightLogo className={className} />
  );
};

export const DarkLogo = (props: ComponentPropsWithoutRef<'img'>) => {
  const { t } = useTranslation();
  const { darkLogo } = useRhinoConfig();
  const darkLogoSource = useRhinoAsset(darkLogo);

  return (
    <img
      alt={t('rhino.frontend.messages.dark_logo', {
        defaultValue: 'Dark Logo'
      })}
      src={darkLogoSource}
      {...props}
    />
  );
};

export const LightLogo = (props: ComponentPropsWithoutRef<'img'>) => {
  const { t } = useTranslation();
  const { lightLogo } = useRhinoConfig();
  const lightLogoSource = useRhinoAsset(lightLogo);

  return (
    <img
      alt={t('rhino.frontend.messages.light_logo', {
        defaultValue: 'Light Logo'
      })}
      src={lightLogoSource}
      {...props}
    />
  );
};

export const SplashScreen = () => {
  return <></>;
};
